//Built-in libraries
import { useEffect, useState } from 'react';

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

//Custom components
import Button from 'components/UI/Button';
import { HookFileInput } from 'components/UI/HookForm/Form/File';

//Services
import { useFileUpload } from 'hooks/useFileUpload';
import { useApplicationFormContext } from 'features/ApplicationForm/ApplicationFormProvider';
import { compareTwoArrays, isObjectEmpty } from 'utils';
import { signApplicationPublicAsset } from 'api/Rover/ApplicationForm';

//STP Libraries

//Assets and Miscellaneous

import { SignedApplicationAssetResponse } from 'types/Shared';
import { ISignApplicationPublicAssetPayload } from 'api/Rover/ApplicationForm/types';
import { HookCheckbox } from 'components/UI/HookForm/Form/Checkbox';
import { HookTextarea } from 'components/UI/HookForm/Form/TextArea';

//Component Begin

export default function Attachments() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const {
    setCurrentStep,
    updateData,
    data: formContextData,
  } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
    watch,
  } = useFormContext();

  const data = watch('step5');
  
  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        res.forEach((r, resIndex) => {
          const { files, sign } = r.value;
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          updateData({
            step5: {
              ...getValues('step5'),
              [payloads[resIndex].field.key]: {
                ...getValues('step5')[payloads[resIndex].field.key],
                selectedFiles: signRes.map((sig, index) => ({
                  name: files[index].name,
                  path: sig.path,
                  key: sig.key,
                  type: files[index].type,
                  field: payloads[resIndex].field.key,
                  description: payloads[resIndex].field.description,
                })),
              },
            },
          });
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step5 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step5)[0];
      const firstErrorChildKey = Object.keys(errors?.step5[firstErrorKey])[0];
      setFocus(`step5.${firstErrorKey}.${firstErrorChildKey}`);
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step5');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step5[
          f
        ].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);

        const fileUploadCompare = compareTwoArrays(
          formDataSelectedFiles,
          formContextDataUploadedFiles
        );

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();
    if (!isValid) {
      return;
    }
    const formData = getValues('step5');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step5: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
    // updateData({
    //   step5: { ...formData },
    // });
    // setCurrentStep((prev) => prev + 1);
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>
          Attachments
        </h3>
        <p className='mt-1 text-sm text-gray-500'>
          Please provide the following documents. If you do not have the
          requested document, please explain why:
        </p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f1.selectedFiles'
            label='Drawing and Specifications'
            id='selectedFiles'
            multiple
          />
        </div>
        {data?.f1?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox
              name='step5.f1.haveFiles'
              id='have-files-f1'
              className='col-span-6 self-end'
            >
              <label htmlFor='have-files-f1'>
                I do not have drawing and specifications
              </label>
            </HookCheckbox>
            {data?.f1?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f1.haveFilesDescription'
                  label='Please describe why you do not have drawings and specifications'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f2.selectedFiles'
            label='Building Photographs'
            id='selectedFiles'
            multiple
          />
        </div>
        {data?.f2?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox
              name='step5.f2.haveFiles'
              id='have-files-f2'
              className='col-span-6 self-end'
            >
              <label htmlFor='have-files-f2'>
                I do not have photographs of building
              </label>
            </HookCheckbox>
            {data?.f2?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f2.haveFilesDescription'
                  label='Please describe why you do not have building photographs'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f3.selectedFiles'
            label='Letters of commitment (others involved in financing)'
            id='selectedFiles'
            multiple
          />
        </div>
        {data?.f3?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox
              name='step5.f3.haveFiles'
              id='have-files-f3'
              className='col-span-6 self-end'
            >
              <label htmlFor='have-files-f3'>
                I do not have letter of commitment
              </label>
            </HookCheckbox>
            {data?.f3?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f3.haveFilesDescription'
                  label='Please describe why you do not have letters of commitment'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f4.selectedFiles'
            label='Personal financial statements for each building owner.'
            id='selectedFiles'
            multiple
          />
        </div>
        {data?.f4?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox
              name='step5.f4.haveFiles'
              id='have-files-f4'
              className='col-span-6 self-end'
            >
              <label htmlFor='have-files-f4'>
                I do not have financial statements
              </label>
            </HookCheckbox>
            {data?.f4?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f4.haveFilesDescription'
                  label='Please describe why you do not have personal financial statements'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        {uploadError && (
          <p className='text-tertiaryColor-normal text-md col-span-6'>
            {uploadError}
          </p>
        )}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label='Previous'
          btnStyle='neutral'
          type='button'
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
